import { MutationHandlingServiceV2, QueryHandlingService } from '@akebono/core';
import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { first, map, Observable, of, shareReplay, startWith, Subject, takeUntil, tap } from 'rxjs';

import {
  AuctionQuery,
  EdostCitiesGQL,
  EdostCitiesQuery,
  EdostCitiesQueryVariables,
  ExchangeRate,
  RussianDeliveryCalculationCreateGQL,
  RussianDeliveryCalculationCreateInput,
  RussianDeliveryCalculationCreateMutation,
  RussianDeliveryCalculationCreateMutationVariables,
} from '../../../../graphql/user-service';

type Rate = {
  company: string;
  name: string;
  price: number;
};

export enum DestinationEnum {
  RU = 'ru',
  CIS = 'cis',
}

@Component({
  selector: 'app-delivery-calculator',
  templateUrl: './delivery-calculator.component.html',
  styleUrls: ['./delivery-calculator.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeliveryCalculatorComponent implements OnInit, OnDestroy {
  @Input() currentUser: AuctionQuery['currentUser'] = null;
  @Input() exchangeRate: ExchangeRate;
  @Input() set initialWeight(value: number) {
    const weightControl = this.form.get('weight')!;
    if (weightControl.untouched || weightControl.value === null) {
      weightControl.setValue(value);
    }
  }

  form: FormGroup;
  loading$ = of(false);
  rates$: Observable<Rate[] | null> = of(null);
  allCities$: Observable<EdostCitiesQuery['edostRuCities']> = of([]);
  filteredCities$: Observable<EdostCitiesQuery['edostRuCities']> = of([]);

  destinations: { value: DestinationEnum; label: string }[] = [
    { value: DestinationEnum.RU, label: 'AUCTION.DELIVERY_CALC.RU' },
    { value: DestinationEnum.CIS, label: 'AUCTION.DELIVERY_CALC.CIS' },
  ];

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private mhs: MutationHandlingServiceV2,
    private qhs: QueryHandlingService,
    private russianDeliveryCalculationCreateGQL: RussianDeliveryCalculationCreateGQL,
    private edostCitiesGQL: EdostCitiesGQL,
    private translateService: TranslateService,
  ) {
    this.form = this.fb.group({
      destination: this.fb.control(DestinationEnum.RU, Validators.required),
      city: this.fb.control(null, Validators.required),
      insurance: this.fb.control(null, Validators.required),
      weight: this.fb.control(null, [Validators.min(0.1)]),
      zip: this.fb.control(null),
      length: this.fb.control(null),
      width: this.fb.control(null),
      height: this.fb.control(null),
    });
  }

  ngOnInit(): void {
    const queryRef = this.qhs.fetch<EdostCitiesQuery, EdostCitiesQueryVariables>(
      this.edostCitiesGQL,
      {},
      'cache-first',
    );

    this.allCities$ = queryRef.data.pipe(
      map((data) => data?.edostRuCities || []),
      shareReplay({ bufferSize: 1, refCount: true }),
      takeUntil(this.destroy$),
    );

    this.allCities$.pipe(first(), takeUntil(this.destroy$)).subscribe((cities) => {
      const address = this.currentUser?.addresses?.find((a) => a.default);

      if (address?.city) {
        const foundCity = cities.find((city) => this.isTargetCity(city, address.city));

        if (foundCity) {
          this.form.patchValue({
            destination: foundCity.cisCountry === 'ru' ? DestinationEnum.RU : DestinationEnum.CIS,
            city: foundCity.code,
            zip: address.zip,
          });
        }
      }
    });

    this.form
      .get('destination')
      ?.valueChanges.pipe(takeUntil(this.destroy$), startWith(this.form.value.destination))
      .subscribe((destination) => {
        this.updateFilteredCities(destination);
        this.form.patchValue({ city: null }, { emitEvent: false });
      });
  }

  private updateFilteredCities(destination: DestinationEnum, searchValue?: string): void {
    const search = searchValue?.toLowerCase() || '';
    const countryType = destination === DestinationEnum.RU ? 'ru' : 'cis';

    this.filteredCities$ = this.allCities$.pipe(
      map((cities) => {
        let filtered = cities.filter((city) => city.cisCountry === countryType);
        if (search) {
          filtered = filtered.filter((city) => this.isTargetCity(city, search));
        }
        return filtered;
      }),
    );
  }

  private isTargetCity(
    city: EdostCitiesQuery['edostRuCities'][number],
    targetCityName: string | null | undefined,
  ): boolean {
    return [city.nameRu, city.nameEn, city.nameWithRegionRu, city.nameWithRegionEn]
      .join(' ')
      .toLowerCase()
      .includes(targetCityName?.toLocaleLowerCase() || '');
  }

  calculate(): void {
    if (!this.form.valid) {
      return;
    }

    const formValue = this.form.value;
    const input: RussianDeliveryCalculationCreateInput = {
      toCity: formValue.city.trim(),
      weight: formValue.weight,
      strah: formValue.insurance,
      zip: Number(formValue.zip),
      hg: formValue.height,
      ln: formValue.length,
      wd: formValue.width,
    };

    const mutationRef = this.mhs.mutate<
      RussianDeliveryCalculationCreateMutation,
      RussianDeliveryCalculationCreateMutationVariables
    >(this.russianDeliveryCalculationCreateGQL, { input }, { renderSuccess: false });

    this.loading$ = mutationRef.loading;
    this.rates$ = mutationRef.data.pipe(
      first(),
      map((data) => data.russianDeliveryCalculationCreate?.calculation),
      map((data) => data?.rsp?.tarif || []),
      map((rates) => rates as Rate[]),
      tap((rates) => {
        rates.sort((a, b) => {
          if (a.company > b.company) {
            return -1;
          }
          if (b.company > a.company) {
            return 1;
          }
          return 0;
        });
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
      takeUntil(this.destroy$),
    );
  }

  getRateName(rate: Rate): string {
    return `${rate.company} ${rate.name}`.trim();
  }

  getCityDisplayName(item: EdostCitiesQuery['edostRuCities'][number]): string {
    const currentLang = this.translateService.currentLang;
    return currentLang === 'ru' ? item.nameWithRegionRu || '' : item.nameWithRegionEn || '';
  }

  getCityValue(item: EdostCitiesQuery['edostRuCities'][number]): string {
    return item.code || '';
  }

  onCitySearch(searchValue: string): void {
    this.updateFilteredCities(this.form.value.destination, searchValue);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
